import React, { useState, useEffect, useCallback } from 'react';
import { HeaderLeft } from '../../components/layout/HeaderSlot';
import { FiHome, FiChevronRight, FiUser, FiShield } from 'react-icons/fi';
import ProfilePage from '../profile/ProfilePage';
import { ManageAccountAccessTab } from '../bookkeeping/components/FamilyBusinessesPage';
import { confirmDiscardChanges, setFormDirty } from '../../lib/unsavedChanges';
import '../bookkeeping/components/BookkeepingPage.css';
import './SettingsPage.css';

// =============================================
// Settings — site-wide account settings (reached via the gear at the
// bottom of the left sidebar). Home for cross-module utilities.
// =============================================

type SettingsTab = 'profile' | 'account-permissions';

const TABS: { key: SettingsTab; label: string; icon: React.ReactNode }[] = [
  { key: 'profile',             label: 'My Profile',          icon: <FiUser size={15} /> },
  { key: 'account-permissions', label: 'Account Permissions', icon: <FiShield size={15} /> },
];

function resolveTabFromHash(): SettingsTab {
  const hash = window.location.hash.replace('#', '');
  return TABS.some(t => t.key === hash) ? (hash as SettingsTab) : 'profile';
}

const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<SettingsTab>(resolveTabFromHash());
  // Bumped to remount (reset to landing) when the active tab is re-clicked.
  const [tabResetKey, setTabResetKey] = useState(0);

  // Keep the URL hash in sync so the tab survives refresh / back-forward
  useEffect(() => {
    const newHash = '#' + activeTab;
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search + newHash);
    }
  }, [activeTab]);

  useEffect(() => {
    const onHashChange = () => setActiveTab(resolveTabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const switchTab = useCallback(async (tab: SettingsTab) => {
    if (!(await confirmDiscardChanges())) return;
    setFormDirty(false);
    // Re-clicking the active tab resets it to its landing (remount); otherwise switch.
    if (tab === activeTab) setTabResetKey(k => k + 1);
    else setActiveTab(tab);
  }, [activeTab]);

  const activeLabel = TABS.find(t => t.key === activeTab)?.label ?? '';

  return (
    <div className="settings-page">
      <HeaderLeft>
<nav className="bk-breadcrumbs" aria-label="Breadcrumb">
        <span className="bk-breadcrumb-item bk-breadcrumb-link" onClick={async () => { if (!(await confirmDiscardChanges())) return; setFormDirty(false); window.location.href = '/dashboard'; }}>
          <FiHome size={13} /> Home
        </span>
        <FiChevronRight size={12} className="bk-breadcrumb-sep" />
        <span className="bk-breadcrumb-item bk-breadcrumb-current">Settings</span>
        <FiChevronRight size={12} className="bk-breadcrumb-sep" />
        <span className="bk-breadcrumb-item bk-breadcrumb-current">{activeLabel}</span>
      </nav>
</HeaderLeft>

      <div className="settings-header">
        <h2>Settings</h2>
        <p>Manage your profile and who can access your account.</p>
      </div>

      <div className="settings-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`settings-tab ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => switchTab(t.key)}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div className="settings-content">
        <React.Fragment key={`${activeTab}-${tabResetKey}`}>
          {activeTab === 'profile' && <ProfilePage />}
          {activeTab === 'account-permissions' && <ManageAccountAccessTab />}
        </React.Fragment>
      </div>
    </div>
  );
};

export default SettingsPage;
